import { useCallback, useEffect, useId, useMemo, useRef, useState, type DragEvent } from 'react';
import '../shared-table-room.css';
import { Link } from 'react-router';
import { collabGroupOpened, type AnalyticsEvent } from '../../core/events';
import {
  call, COLLAB_UNREACHABLE, createInvitation as postInvitation, saveStandardsDefaults, deleteMaterial, downloadMaterial,
  fetchMaterialPreview, fetchThumbnail, fetchTrash, purgeMaterial, renameGroup, resendInvitation as postResend,
  restoreMaterial,
  type ApiResult, type GetToken, type GroupSummary, type MaterialSummary,
  type Member, type PendingInvitation, type Saver, type TrashedMaterial,
} from '../api';
import {
  CONTEXT_FIELD_METADATA, CONTEXT_FIELDS, type ContextField, type StandardsContext,
} from '../../core/standards-context';
import { pdfJsEngine, type PdfEngine } from '../pdf';
import { groupsPath, materialPath, newMaterialPath } from '../routes';
import { STUDENT_RECORDS_NOTICE } from '../upload-copy';
import { useUploads } from '../uploads';
import { makeThumbnail, thumbnailCtx, useThumbnailBackfill } from '../thumbnails';
import InviteDialog from './InviteDialog';
import {
  fanOut, INVITE_CONCURRENCY, settleInvite, type InviteResult, type SkippedAddress,
} from './InviteResults';
import MaterialList, { avatarInitials, type FetchState } from './MaterialList';
import InlineRename from './InlineRename';
import RecentlyDeleted from './RecentlyDeleted';
import StartedGroupPanel, { type StartedGroup } from './StartedGroupPanel';

/**
 * One group: who is in it, and what is in it.
 *
 * The material and its conversation used to live at the bottom of this screen. They are a
 * route of their own now (`MaterialPage`), because opening a material is somewhere a teacher
 * *goes*: the back button should bring them back here, and the address of a worksheet should
 * be something they can send a colleague in the same group.
 *
 * So did the upload form, for the same reason and one more: it was the loudest thing on a
 * screen whose subject is what the group has *already* shared. Adding one is a destination now
 * (`MaterialUploadPage`, at `/g/:groupId/new`) and this screen's main content is the list,
 * with one primary control pointing at it.
 *
 * Three rules the rest of this file is built around:
 *
 * 1. **Every control is gated by the role the API returned**, never by a local guess. The
 *    Worker re-checks membership on every request (R5) and a browser that decides
 *    differently would only ever be wrong in one of two ways: hiding something a teacher may
 *    do, or offering something they may not and letting them find out by failing.
 *
 * 2. **A 404 on an open session is a stated state.** Membership can be removed while this
 *    screen is on a teacher's monitor (R3, AE4), and the next request answers 404. Leaving
 *    the roster and the material list on screen after that would show a group the teacher
 *    can no longer reach and cannot refresh. So it is replaced, and it says what happened.
 *
 * 3. **A 404 on the *first* load is "we could not find that group".** With real URLs a
 *    teacher can arrive here cold, by link or by typing, and the Worker answers the same 404
 *    for a group that does not exist and one they are not in (R5, AE1). That single answer is
 *    not re-derived here; what is distinguished is only *when* it arrived, which is a fact
 *    about the session rather than about the id.
 */

export interface GroupRoomProps {
  /** From the URL. The summary below is a head start, never the authority. */
  groupId: string;
  /** What the group list already knew, when the teacher came from it. Absent on a cold load. */
  group?: GroupSummary;
  getToken: GetToken;
  viewerUserId: string | null;
  track: (event: AnalyticsEvent) => void;
  onBack: () => void;
  /**
   * What came of starting this group a moment ago, when the teacher arrived from `/new`.
   *
   * Absent on every other arrival, including a refresh once it has been dismissed. It is what
   * makes a partly-failed creation land *inside the group* rather than on an error screen:
   * the group exists, and this panel is where its links and its refused addresses are.
   */
  started?: StartedGroup | null;
  /** Dismissing that panel — which also drops the links out of the history entry. */
  onStartedDone?: () => void;
  /** Injected in tests so a download asserts without a real anchor click. */
  saver?: Saver;
  /** Injected in tests so "Goes for good in N days" and the invitation wait labels are fixed. */
  now?: () => Date;
  /** The PDF engine a background thumbnail is drawn with. Injected in tests. */
  pdf?: PdfEngine;
  /** False when the unread kill switch is on. Markers show by default (R11). */
  showUnread?: boolean;
}

/** The Worker's `MAX_NAME_LENGTH`, so the field stops where the Worker would refuse. */
const GROUP_NAME_MAX = 120;

export default function GroupRoom({
  groupId, group: initial, getToken, viewerUserId, track, onBack,
  started = null, onStartedDone, saver, now, pdf = pdfJsEngine, showUnread = true,
}: GroupRoomProps) {
  const [group, setGroup] = useState<GroupSummary | null>(initial ?? null);
  const [accessLost, setAccessLost] = useState(false);
  const [missing, setMissing] = useState(false);

  const [members, setMembers] = useState<Member[]>([]);
  const [membersState, setMembersState] = useState<FetchState>('loading');

  const [materials, setMaterials] = useState<MaterialSummary[]>([]);
  const [materialsState, setMaterialsState] = useState<FetchState>('loading');

  const [inviting, setInviting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [invitationsState, setInvitationsState] = useState<FetchState>('loading');
  /**
   * One row per address from the latest paste, plus any resend since. Replaced, not appended,
   * by the next paste — as the single link was before — so a row always answers the list the
   * admin can still see they sent. Anything they lose sight of is a Resend away.
   */
  const [inviteResults, setInviteResults] = useState<InviteResult[]>([]);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [downloadError, setDownloadError] = useState<string | null>(null);

  // The trash: what this teacher may restore, the Undo on offer, and what went wrong.
  const [trash, setTrash] = useState<TrashedMaterial[]>([]);
  const [undo, setUndo] = useState<MaterialSummary | null>(null);
  const [trashBusy, setTrashBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [trashError, setTrashError] = useState<string | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);

  // Several files at once: dropped anywhere on the room, or picked from "Add files".
  // The queue itself lives above the routes, so it outlives this screen.
  const uploads = useUploads();
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);

  const [tab, setTab] = useState<'materials' | 'people'>('materials');
  const roomId = useId();

  const isAdmin = group?.role === 'admin';

  /**
   * Whether this room ever rendered. Before it has, a 404 is the only answer we ever got and
   * reads as "not found"; after it has, a 404 is something that changed underneath the page.
   */
  const settled = useRef(initial !== undefined);

  /** The one place a 404 turns into the stated state. Every load and every write uses it. */
  const lost = useCallback(() => {
    if (settled.current) setAccessLost(true);
    else setMissing(true);
  }, []);

  const loadGroup = useCallback(async () => {
    const result = await call<{ group: GroupSummary }>(getToken, `/api/groups/${encodeURIComponent(groupId)}`);
    if (result.ok) { setGroup(result.data.group); settled.current = true; }
    else if (result.status === 404) lost();
  }, [getToken, groupId, lost]);

  const loadMembers = useCallback(async () => {
    setMembersState('loading');
    const result = await call<{ members: Member[] }>(getToken, `/api/groups/${encodeURIComponent(groupId)}/members`);
    if (!result.ok) {
      if (result.status === 404) lost();
      setMembersState('failed');
      return;
    }
    setMembers(result.data.members);
    setMembersState('ready');
  }, [getToken, groupId, lost]);

  /*
   * Reloads overlap: a delete, an undo and a finished upload can each ask while another is
   * in flight, and answers arrive in any order. Each load takes a ticket, and only the
   * newest ticket's answer is applied, so an earlier list never overwrites a later one.
   */
  const materialsTicket = useRef(0);
  const trashTicket = useRef(0);

  const loadMaterials = useCallback(async () => {
    const ticket = ++materialsTicket.current;
    setMaterialsState('loading');
    const result = await call<{ materials: MaterialSummary[] }>(getToken, `/api/groups/${encodeURIComponent(groupId)}/materials`);
    if (ticket !== materialsTicket.current) return;
    if (!result.ok) {
      if (result.status === 404) lost();
      setMaterialsState('failed');
      return;
    }
    setMaterials(result.data.materials);
    setMaterialsState('ready');
  }, [getToken, groupId, lost]);

  /** Quiet on failure: the section is a way back, and the list above it still works. */
  const loadTrash = useCallback(async () => {
    const ticket = ++trashTicket.current;
    const result = await fetchTrash(getToken, groupId);
    if (ticket !== trashTicket.current) return;
    if (result.ok) setTrash(result.data.materials);
    else if (result.status === 404) lost();
  }, [getToken, groupId, lost]);

  const loadInvitations = useCallback(async () => {
    setInvitationsState('loading');
    const result = await call<{ invitations: PendingInvitation[] }>(getToken, `/api/groups/${encodeURIComponent(groupId)}/invitations`);
    if (!result.ok) {
      // A demoted admin gets 403 here and 404 if they were removed outright. Neither is a
      // fetch that can be retried into working, so both render the failed state.
      if (result.status === 404) lost();
      setInvitationsState('failed');
      return;
    }
    setInvitations(result.data.invitations);
    setInvitationsState('ready');
  }, [getToken, groupId, lost]);

  useEffect(() => {
    void loadGroup();
    void loadMembers();
    void loadMaterials();
    void loadTrash();
  }, [loadGroup, loadMembers, loadMaterials, loadTrash]);

  useEffect(() => {
    if (!inviteOpen) return;
    void loadInvitations();
  }, [inviteOpen, loadInvitations]);

  /**
   * A file the panel finished for this group appears in the list without a reload. Counted by
   * rows holding a material, so it fires on the 2xx — before any thumbnail — and once per file.
   */
  const mine = uploads?.items.filter((item) => item.groupId === groupId && item.material !== null) ?? [];
  // And once more when its thumbnail step ends, so the card shows the picture. A file with
  // no thumbnail step lands and finishes in one render, which is one reload, not two.
  const landed = mine.length + mine.filter((item) => item.state === 'done').length;
  const seenLanded = useRef(landed);
  useEffect(() => {
    if (landed > seenLanded.current) void loadMaterials();
    seenLanded.current = landed;
  }, [landed, loadMaterials]);

  /*
   * Thumbnails the room fills in: the viewer's own PDFs and pictures that have none, one
   * at a time, a few per visit, after the list has painted — skipping any the upload panel is
   * drawing right now. Silent: a card without one shows its icon.
   */
  const drawingKey = mine.filter((item) => item.state === 'thumbnail').map((item) => item.material?.id).join(',');
  const drawing = useMemo(() => new Set(drawingKey ? drawingKey.split(',') : []), [drawingKey]);
  useThumbnailBackfill({
    groupId,
    materials,
    ready: materialsState === 'ready',
    viewerUserId,
    busy: drawing,
    fetchSource: async (material) => {
      const result = await fetchMaterialPreview(getToken, material);
      return result.ok ? result.data : null;
    },
    make: (materialId, source) => makeThumbnail(thumbnailCtx(getToken, pdf), materialId, source),
    onMade: (materialId, pageCount) => setMaterials((list) => list.map((material) => (
      material.id === materialId ? { ...material, hasThumbnail: true, pageCount } : material
    ))),
  });

  // Once per open group, and not twice under StrictMode's double invocation.
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (membersState !== 'ready' || materialsState !== 'ready') return;
    if (announced.current === groupId) return;
    announced.current = groupId;
    track(collabGroupOpened(groupId, members.length, materials.length));
  }, [groupId, membersState, materialsState, members.length, materials.length, track]);

  /** One address's request, settled into its own row. A 404 is the group, not the address. */
  async function inviteOne(email: string) {
    const result = await postInvitation(getToken, groupId, email);
    if (!result.ok && result.status === 404) { lost(); return; }
    setInviteResults((rows) => rows.map((row) => (
      row.email === email && row.state === 'inviting' ? settleInvite(email, result) : row
    )));
  }

  /**
   * A pasted list (R1, R2). Every address is shown as "Inviting…" at once and resolves on its
   * own, four in flight (KTD4) so a slow mail call holds up its own row rather than the paste.
   * The skipped ones were decided in the dialog and are only reported here.
   */
  async function inviteMany(emails: string[], skipped: SkippedAddress[]) {
    setInviting(true);
    setInviteError(null);
    setInviteResults([
      ...emails.map((email): InviteResult => ({ email, state: 'inviting' })),
      ...skipped.map((entry): InviteResult => ({ email: entry.email, state: 'skipped', reason: entry.reason })),
    ]);
    await fanOut(emails, INVITE_CONCURRENCY, inviteOne);
    setInviting(false);
    await loadInvitations();
  }

  /** The per-row retry: a 429, a network blip or a refusal, tried again for that address only. */
  async function retryInvite(email: string) {
    setInviting(true);
    setInviteResults((rows) => rows.map((row) => (
      row.email === email && row.state === 'failed' ? { email, state: 'inviting' } : row
    )));
    await inviteOne(email);
    setInviting(false);
    await loadInvitations();
  }

  /**
   * A new link for the same address (R8). The Worker revokes the old one in the same batch, so
   * the row is replaced on the spot — one row per address (KTD10) — before the reload confirms
   * it, and the new link's result joins the rows above with the same copy a paste gets.
   */
  async function resend(invitation: PendingInvitation) {
    setInviting(true);
    setInviteError(null);
    const result = await postResend(getToken, groupId, invitation.id);
    setInviting(false);

    if (!result.ok) {
      // 404 here is the row, not the group: consumed or already replaced since the list loaded.
      setInviteError(result.status === 429
        ? 'Too many invitations just now — try this one again in a minute'
        : result.status === 404
          ? 'That invitation is no longer there to resend. The list has been refreshed.'
          : 'We could not resend that invitation. Try again in a moment.');
      await loadInvitations();
      return;
    }

    if (result.data.result === 'invited') {
      const fresh = result.data.invitation;
      setInvitations((rows) => rows.map((row) => (row.id === invitation.id ? {
        ...row, id: fresh.id, createdAt: fresh.createdAt, expiresAt: fresh.expiresAt,
        lastRefusal: null, declinedAt: null, invitedBy: viewerUserId ?? row.invitedBy,
      } : row)));
    }
    setInviteResults((rows) => [
      settleInvite(invitation.email, result),
      ...rows.filter((row) => row.email !== invitation.email),
    ]);
    await loadInvitations();
  }

  async function revokeInvitation(invitation: PendingInvitation) {
    setInviting(true);
    const result = await call(getToken, `/api/groups/${encodeURIComponent(groupId)}/invitations/${encodeURIComponent(invitation.id)}`, {
      method: 'DELETE',
    });
    setInviting(false);
    if (!result.ok && result.status === 404) { lost(); return; }
    // A revoked link must not stay on screen looking like one that works.
    setInviteResults((rows) => rows.filter((row) => !(row.state === 'invited' && row.invitation.id === invitation.id)));
    await loadInvitations();
  }

  async function removeMaterial(material: MaterialSummary) {
    setDeleteError(null);
    const result = await deleteMaterial(getToken, material.id);
    if (!result.ok) {
      if (result.status === 404) { lost(); return; }
      setDeleteError('We could not delete that material. Try again in a moment.');
      return;
    }
    setUndo(material);
    await Promise.all([loadMaterials(), loadTrash()]);
  }

  /** What a refused restore says. `group_full` is the one the teacher can do something about. */
  const restoreRefusal = (reason: string | undefined) => (reason === 'group_full'
    ? 'This group is full. Delete something else first, then restore this.'
    : 'We could not restore that material. Try again in a moment.');

  /**
   * Undo and Restore are one request. A 404 here means the material is no longer in the
   * trash — purged, or past its thirty days — not that the group is gone, so it is said as
   * an error and the lists are refreshed.
   */
  async function restore(material: MaterialSummary, report: (message: string) => void) {
    setTrashBusy(true);
    const result = await restoreMaterial(getToken, material.id);
    setTrashBusy(false);
    setUndo(null);
    if (!result.ok) report(restoreRefusal(result.reason));
    await Promise.all([loadMaterials(), loadTrash()]);
  }

  async function purge(material: TrashedMaterial) {
    setTrashError(null);
    setTrashBusy(true);
    const result = await purgeMaterial(getToken, material.id);
    setTrashBusy(false);
    if (!result.ok) setTrashError('We could not delete that for good. Try again in a moment.');
    if (undo?.id === material.id) setUndo(null);
    await Promise.all([loadMaterials(), loadTrash()]);
  }

  async function download(material: MaterialSummary) {
    setDownloadError(null);
    const result = await downloadMaterial(getToken, material, saver);
    if (result.ok) return;
    if (result.status === 404) { lost(); return; }
    setDownloadError('We could not download that material. Try again in a moment.');
  }

  async function removeMember(member: Member) {
    setRosterError(null);
    const result = await call(getToken, `/api/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(member.userId)}`, {
      method: 'DELETE',
    });
    if (!result.ok) {
      if (result.status === 404 && member.userId === viewerUserId) { lost(); return; }
      setRosterError(result.error === 'last_admin'
        ? 'A group needs at least one admin. Promote someone else first.'
        : 'That change was not made. Try again in a moment.');
      return;
    }
    if (member.userId === viewerUserId) { onBack(); return; }
    await Promise.all([loadMembers(), loadGroup()]);
  }

  async function changeRole(member: Member, role: 'admin' | 'member') {
    setRosterError(null);
    const result = await call(getToken, `/api/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(member.userId)}/role`, {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ role }),
    });
    if (!result.ok) {
      setRosterError(result.error === 'last_admin'
        ? 'A group needs at least one admin. Promote someone else first.'
        : 'That change was not made. Try again in a moment.');
      return;
    }
    await Promise.all([loadMembers(), loadGroup()]);
  }

  if (accessLost) {
    return (
      <section className="panel" aria-label="Group">
        <h2>You no longer have access to this group.</h2>
        <p>
          Your membership ended while this page was open. Anything you shared stays with the
          group.
        </p>
        <button type="button" onClick={onBack}>Back to your groups</button>
      </section>
    );
  }

  if (missing) {
    // A link to a group that is not there, or not theirs. One answer for both (R5, AE1): it
    // names nothing and tells whoever holds the address nothing they did not already have.
    return (
      <section className="panel" aria-label="Group">
        <h2>We could not find that group.</h2>
        <p>It may have been removed, or the link may be for a group you are not in.</p>
        <Link className="link" to={groupsPath()}>Back to your groups</Link>
      </section>
    );
  }

  if (group === null) {
    return (
      <section className="panel" aria-label="Group">
        <p className="hint" role="status">Opening this group…</p>
      </section>
    );
  }

  /**
   * Who shared this, in the vocabulary the thread already uses.
   *
   * The roster is the group's own answer about who is in it, so it is what an uploader's id is
   * resolved against — never a Clerk lookup, and never a second source of truth about names.
   * Someone who has since left is "Former member", exactly as R18 labels them on a comment.
   */
  const sharedBy = (material: MaterialSummary): string => {
    if (material.uploadedBy === viewerUserId) return 'You';
    return members.find((member) => member.userId === material.uploadedBy)?.email
      ?? 'Former member';
  };

  /**
   * Rename the group, for an admin. The answer is the group as `GET` shows it, so it replaces
   * what is held. A 403 means the role changed underneath the page, so the group is read
   * again and the control goes with it.
   */
  const rename = async (name: string): Promise<string | null> => {
    const result = await renameGroup(getToken, groupId, name);
    if (result.ok) { setGroup(result.data.group); return null; }
    if (result.status === 404) { lost(); return null; }
    if (result.status === 403) { void loadGroup(); return 'Only an admin can rename this group.'; }
    if (result.status === 400) return `Use a name of up to ${GROUP_NAME_MAX} characters.`;
    return COLLAB_UNREACHABLE;
  };

  const addFiles = uploads ? (files: File[]) => uploads.add(groupId, files) : undefined;

  /*
   * Dropping files anywhere on the room. `dragenter`/`dragleave` fire for every child the
   * pointer crosses, so a depth count — not the last event — decides when the overlay goes.
   * Only a drag that carries files counts; dragging a link or some text past is not a drop.
   */
  const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files');
  const dropHandlers = addFiles ? {
    onDragEnter: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (event: DragEvent) => { if (carriesFiles(event)) event.preventDefault(); },
    onDragLeave: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    },
    onDrop: (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      addFiles(Array.from(event.dataTransfer.files));
    },
  } : {};

  return (
    <section aria-label="Group" className={`table-room collab-room${dragging ? ' is-dragging' : ''}`} {...dropHandlers}>
      {dragging && (
        // R15 at the point of upload, here too: the rule is on screen while the files are
        // over the room, which is the moment they are being chosen.
        <div className="collab-drop" aria-hidden="true">
          <p className="collab-drop-title">Drop to share with this group</p>
          <p className="notice">{STUDENT_RECORDS_NOTICE}</p>
        </div>
      )}
      <aside className="table-sidebar" aria-label="Group navigation">
        <p className="table-eyebrow">Your shared table</p>
        <button type="button" className="secondary table-back" onClick={onBack}>Back to your groups</button>
        <p className="table-current collab-group-name" aria-current="page">{group.name}</p>
      </aside>
      <div className="table-main">
      <header className="table-hero">
        <p className="table-eyebrow">Made together</p>
        <InlineRename
          canRename={isAdmin}
          value={group.name}
          noun="group"
          label="Group name"
          maxLength={GROUP_NAME_MAX}
          onSave={rename}
        >
          <h2 className="collab-group-name">{group.name}</h2>
        </InlineRename>
        <p>Good ideas belong at the table.</p>
        <div className="table-group-facts">
          <div className="table-avatar-stack collab-member-roster" aria-hidden="true">
            {members.slice(0, 4).map((member) => <span className="table-avatar" key={member.userId}>{avatarInitials(member.email)}</span>)}
          </div>
          <span>{group.memberCount} {group.memberCount === 1 ? 'member' : 'members'} · you are{group.role === 'admin' ? ' an admin' : ' a member'}</span>
        </div>
        {isAdmin && (
          <button type="button" className="secondary table-invite" onClick={() => {
            setTab('people');
            setInviteOpen((open) => !open);
          }}>
            {inviteOpen ? 'Close invitations' : 'Invite a colleague'}
          </button>
        )}
      </header>

      {/* First thing on the screen when this group is seconds old: its links, and anything
          that did not happen while it was being made. */}
      {started && (
        <StartedGroupPanel
          groupId={groupId}
          started={started}
          getToken={getToken}
          onDone={() => onStartedDone?.()}
        />
      )}

      <div className="table-tabs" role="tablist" aria-label="Group sections">
        {(['materials', 'people'] as const).map((name) => (
          <button key={name} id={`${roomId}-${name}-tab`} type="button" role="tab"
            aria-selected={tab === name} aria-controls={`${roomId}-${name}-panel`}
            tabIndex={tab === name ? 0 : -1}
            onClick={() => setTab(name)}
            onKeyDown={(event) => {
              if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                const next = event.key === 'Home' ? 'materials' : event.key === 'End' ? 'people' : tab === 'materials' ? 'people' : 'materials';
                setTab(next);
                document.getElementById(`${roomId}-${next}-tab`)?.focus();
              }
            }}>
            {name === 'materials' ? 'Materials' : 'People'}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${roomId}-people-panel`} aria-labelledby={`${roomId}-people-tab`} hidden={tab !== 'people'}>
      <section className="panel table-roster collab-member-roster" aria-label="Members">
        <h3>Members</h3>

        {membersState === 'loading' && <p className="hint" role="status">Loading members…</p>}

        {membersState === 'failed' && (
          <div role="alert">
            <p className="error">We could not load the members of this group.</p>
            <button type="button" onClick={() => void loadMembers()}>Try again</button>
          </div>
        )}

        {rosterError && <p className="error" role="alert">{rosterError}</p>}

        {membersState === 'ready' && (
          <ul>
            {members.map((member) => (
              <li key={member.userId}>
                {/* Members see each other's addresses — the Key Decision behind R2. */}
                <span className="table-avatar" aria-hidden="true">{avatarInitials(member.email)}</span>
                <span className="table-member-email">{member.email}</span>
                <span className="meta"> {member.role}</span>
                {isAdmin && member.userId !== viewerUserId && (
                  <span className="row-actions">
                    <button
                      type="button"
                      onClick={() => void changeRole(member, member.role === 'admin' ? 'member' : 'admin')}
                    >
                      {member.role === 'admin' ? 'Make a member' : 'Make an admin'}
                    </button>
                    <button type="button" className="danger" onClick={() => void removeMember(member)}>
                      Remove
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {viewerUserId && members.some((member) => member.userId === viewerUserId) && (
          <button
            type="button"
            className="danger"
            onClick={() => {
              const self = members.find((member) => member.userId === viewerUserId);
              if (self) void removeMember(self);
            }}
          >
            Leave this group
          </button>
        )}
      </section>

      <StandardsDefaults
        defaults={group.standardsDefaults ?? null}
        onSave={(ctx) => saveStandardsDefaults(getToken, groupId, ctx)}
        onSaved={() => { void loadGroup(); }}
        onAccessLost={lost}
      />

      {isAdmin && inviteOpen && (
        <InviteDialog
          pending={invitations}
          pendingState={invitationsState}
          results={inviteResults}
          now={(now ?? (() => new Date()))().toISOString()}
          selfEmail={members.find((member) => member.userId === viewerUserId)?.email ?? null}
          busy={inviting}
          error={inviteError}
          onInvite={(emails, skipped) => { void inviteMany(emails, skipped); }}
          onRetryAddress={(email) => { void retryInvite(email); }}
          onResend={(invitation) => { void resend(invitation); }}
          onRevoke={(invitation) => { void revokeInvitation(invitation); }}
          onRetry={() => { void loadInvitations(); }}
          onClose={() => { setInviteOpen(false); setInviteResults([]); }}
        />
      )}

      </div>
      <div role="tabpanel" id={`${roomId}-materials-panel`} aria-labelledby={`${roomId}-materials-tab`} hidden={tab !== 'materials'}>
      <MaterialList
        materials={materials}
        state={materialsState}
        canDelete={(material) => isAdmin || material.uploadedBy === viewerUserId}
        linkTo={(material) => materialPath(groupId, material.id)}
        addHref={newMaterialPath(groupId)}
        {...(addFiles ? { onAddFiles: addFiles } : {})}
        sharedBy={sharedBy}
        onDownload={(material) => { void download(material); }}
        onDelete={(material) => { void removeMaterial(material); }}
        onRetry={() => { void loadMaterials(); }}
        downloadError={downloadError}
        undo={undo && {
          id: undo.id,
          title: undo.title,
          busy: trashBusy,
          onUndo: () => { setDeleteError(null); void restore(undo, setDeleteError); },
          onExpire: () => setUndo(null),
        }}
        deleteError={deleteError}
        loadThumbnail={(material) => fetchThumbnail(getToken, material.id)}
        {...(saver ? { objectUrls: saver } : {})}
        showUnread={showUnread}
      />

      <RecentlyDeleted
        materials={trash}
        busy={trashBusy}
        error={trashError}
        onRestore={(material) => { setTrashError(null); void restore(material, setTrashError); }}
        onPurge={(material) => { void purge(material); }}
        now={now}
      />
      </div>
      </div>
    </section>
  );
}

type DefaultsValues = Record<ContextField, string>;

const defaultsValues = (ctx: StandardsContext | null): DefaultsValues => ({
  jurisdiction: ctx?.jurisdiction ?? '', academicSubject: ctx?.academicSubject ?? '', grade: ctx?.grade ?? '',
});

/**
 * The group's Standards check defaults (R2). Any member may set them; every Standards check in
 * the group starts from them unless a material has its own. An empty field is no default.
 */
function StandardsDefaults({ defaults, onSave, onSaved, onAccessLost }: {
  defaults: StandardsContext | null;
  onSave: (ctx: StandardsContext) => Promise<ApiResult<{ standardsDefaults: StandardsContext | null }>>;
  onSaved: () => void;
  onAccessLost: () => void;
}) {
  const [values, setValues] = useState<DefaultsValues>(() => defaultsValues(defaults));
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  /** Whether the member has changed a field since the last save. Read, never rendered. */
  const dirty = useRef(false);

  // The group arrives, or is re-read, after this mounts. Follow it unless the member is
  // part-way through an edit, which a re-read must not throw away.
  const { jurisdiction = '', academicSubject = '', grade = '' } = defaults ?? {};
  useEffect(() => {
    if (!dirty.current) setValues({ jurisdiction, academicSubject, grade });
  }, [jurisdiction, academicSubject, grade]);

  async function save() {
    setStatus('saving');
    const ctx: StandardsContext = {};
    for (const field of CONTEXT_FIELDS) if (values[field]) (ctx as Record<ContextField, string>)[field] = values[field];
    const result = await onSave(ctx);
    if (!result.ok) {
      if (result.status === 404) { onAccessLost(); return; }
      setStatus('error');
      return;
    }
    setValues(defaultsValues(result.data.standardsDefaults));
    dirty.current = false;
    setStatus('saved');
    onSaved();
  }

  return (
    <section className="panel table-roster" aria-label="Standards defaults">
      <h3>Standards defaults</h3>
      <p className="hint">
        Every Standards check in this group starts from these. A material can keep its own.
      </p>
      <form
        className="standards-defaults"
        onSubmit={(event) => { event.preventDefault(); void save(); }}
      >
        <fieldset disabled={status === 'saving'}>
          {CONTEXT_FIELD_METADATA.map(({ field, label, options }) => (
            <label key={field}>
              {label}
              <select
                value={values[field]}
                onChange={(event) => {
                  setValues((current) => ({ ...current, [field]: event.target.value }));
                  dirty.current = true;
                  setStatus('idle');
                }}
              >
                <option value="">No default</option>
                {options.map((value) => <option key={value}>{value}</option>)}
              </select>
            </label>
          ))}
          <button type="submit" disabled={status === 'saving'}>
            {status === 'saving' ? 'Saving…' : 'Save defaults'}
          </button>
        </fieldset>
      </form>
      {status === 'saved' && <p className="hint" role="status">Saved. New checks in this group start from these.</p>}
      {status === 'error' && (
        <p className="error" role="alert">We could not save the defaults. Try again in a moment.</p>
      )}
    </section>
  );
}
